import { Dialog } from '@angular/cdk/dialog';
import { inject } from '@angular/core';
import { AppointmentService } from '@modules/sales/services/appointment.service';
import { RegistrationType } from '@modules/contact/enums/registration-type.enum';
import { Contact } from '@modules/contact/models/contact.model';
import { ContactService } from '@modules/contact/services/contact.service';
import { CustomerNumberService } from '@modules/contact/services/customer-number.service';
import { KfmImageType } from '@modules/media/enums/kfm-image-type.enum';
import { MediaService } from '@modules/media/services/media.service';
import { Task } from '@modules/sales/models/task.model';
import { InvoiceService } from '@modules/sales/services/invoice.service';
import { OfferService } from '@modules/sales/services/offer.service';
import { TaskTypeService } from '@modules/sales/services/task-type.service';
import { TaskService } from '@modules/sales/services/task.service';
import { AuthService } from '@shared/authentication/auth.service';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { KfmInfoDialogComponent } from '@shared/ui/components/features/dialogs/info-dialog/kfm-info-dialog.component';
import { CloudWorkerSyncService } from '@shared/sync/cloud-worker-sync.service';

export class KfmSalesDetailsEvents {
  private readonly _taskTypeService = inject(TaskTypeService);
  private readonly _appointmentService = inject(AppointmentService);
  private readonly _taskService = inject(TaskService);
  private readonly _offerService = inject(OfferService);
  private readonly _invoiceService = inject(InvoiceService);
  private readonly _contactService = inject(ContactService);
  private readonly _customerNumberService = inject(CustomerNumberService);
  private readonly _blazorSyncService = inject(CloudWorkerSyncService);
  private readonly _authService = inject(AuthService);
  private readonly _mediaService = inject(MediaService);
  private readonly _dialog = inject(Dialog);
  private readonly _guidGenerator = inject(GuidGeneratorService);

  /** Customer was added to sales item */
  public async onCustomerAdded(
    salesItem: Task,
    contact: Contact,
  ): Promise<boolean> {
    if (this._taskTypeService.isAppointment(salesItem)) {
      salesItem.appointment.customer = contact;
      salesItem.customer = contact;

      return await this._appointmentService.update(
        salesItem.appointment,
        undefined,
        salesItem.appointment.customer?.uId,
        salesItem.appointment.location?.uId,
      );
    } else {
      salesItem.customer = contact;

      if (this._taskTypeService.isTask(salesItem)) {
        return await this._taskService.update(salesItem);
      } else if (this._taskTypeService.isInvoice(salesItem)) {
        // For invoices: if the customer has no customer number, generate one
        // and sync the contact to the server BEFORE saving the task.
        // This ensures the FK (CustomerId) is satisfied on the server when
        // the Kaufmann sync sends the task change.
        const customer = await this._contactService.getContactById(contact.uId);
        if (customer) {
          const hasCustomerNumber = customer.registrations?.some(
            (r) => r.registrationType === RegistrationType.CustomerReference,
          );

          if (!hasCustomerNumber) {
            await this._customerNumberService.generateCustomerNumber(customer);
          }

          // Sync the contact (and its new registration) to the server so the
          // Contact record exists there before the Task FK reference is synced.
          const tenantId = await this._authService.getSelectedTenantUId();
          if (tenantId) {
            await this._blazorSyncService.start(tenantId, 'Contact');
          }
        }

        return await this._invoiceService.update(salesItem);
      } else if (this._taskTypeService.isOffer(salesItem)) {
        return await this._offerService.update(salesItem);
      }
    }
  }

  /** Customer was removed from sales item */
  public async onCustomerRemoved(salesItem: Task): Promise<boolean> {
    if (this._taskTypeService.isAppointment(salesItem)) {
      salesItem.appointment.customer = undefined;
      salesItem.customer = undefined;

      return await this._appointmentService.update(
        salesItem.appointment,
        undefined,
        salesItem.appointment.customer?.uId,
        salesItem.appointment.location?.uId,
      );
    } else {
      salesItem.customer = undefined;

      if (this._taskTypeService.isTask(salesItem)) {
        return await this._taskService.update(salesItem);
      } else if (this._taskTypeService.isOffer(salesItem)) {
        return await this._offerService.update(salesItem);
      } else if (this._taskTypeService.isInvoice(salesItem)) {
        return await this._invoiceService.update(salesItem);
      }
    }
  }

  /** Location was added to sales item */
  public async onLocationAdded(
    salesItem: Task,
    contact: Contact,
  ): Promise<boolean> {
    if (this._taskTypeService.isAppointment(salesItem)) {
      salesItem.appointment.location = contact;
      salesItem.location = contact;

      return await this._appointmentService.update(
        salesItem.appointment,
        undefined,
        salesItem.appointment.customer?.uId,
        salesItem.appointment.location?.uId,
      );
    } else {
      salesItem.location = contact;

      if (this._taskTypeService.isTask(salesItem)) {
        return await this._taskService.update(salesItem);
      } else if (this._taskTypeService.isOffer(salesItem)) {
        return await this._offerService.update(salesItem);
      } else if (this._taskTypeService.isInvoice(salesItem)) {
        return await this._invoiceService.update(salesItem);
      }
    }
  }

  /** Book invoice clicked */
  public async onBookInvoiceClicked(
    salesItem: Task,
  ): Promise<{ isError: boolean; warnMsg?: string }> {
    let error = '';
    let warn: string | undefined;

    if (!salesItem.paymentTerm) {
      error = '- Keine Zahlungsbedingung ausgewählt<br>';
    }

    if (!salesItem.customer) {
      error += '- Kein Kunde ausgewählt<br>';
    } else {
      const customer = await this._contactService.getContactById(
        salesItem.customer.uId,
      );

      if (customer) {
        const customerNumberRegistration = customer.registrations?.find(
          (r) => r.registrationType === RegistrationType.CustomerReference,
        );

        if (!customerNumberRegistration) {
          // No customer number — generate one client-side (MAX+1) and persist
          // it to local IndexedDB immediately.  The PDF reads from local DB so
          await this._customerNumberService.generateCustomerNumber(customer);
        } else if (!customerNumberRegistration.registrationNumber?.trim()) {
          // Registration exists but the number is still empty.
          // This means the number was never generated client-side — show error.
          this._dialog.open(KfmInfoDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            data: {
              title: 'Kundennummer nicht verfügbar',
              text: 'Die Kundennummer konnte nicht ermittelt werden. Bitte speichern Sie den Kontakt erneut und versuchen Sie es erneut.',
            },
          });
          return { isError: true };
        }
      }
    }

    if (!salesItem.items || salesItem.items.length === 0) {
      error += '- Es sind keine Positionen vorhanden<br>';
    } else {
      let totalPrice = 0;

      salesItem.items.forEach((item) => {
        if (item.type === 0 || item.type == null) {
          totalPrice += item.amount * (item.priceTotal?.netTotal ?? 0);
        }
      });

      salesItem.items.forEach((item) => {
        if (item.type === 2 || item.type === 3) {
          const sign = item.type === 2 ? -1 : 1;
          if (item.percentage) {
            const refTotal = (item.itemIds ?? []).reduce((sum, id) => {
              const refItem = salesItem.items.find(
                (i) => i.uId === id && (i.type === 0 || i.type == null),
              );
              return (
                sum +
                (refItem
                  ? refItem.amount * (refItem.priceTotal?.netTotal ?? 0)
                  : 0)
              );
            }, 0);
            totalPrice += sign * refTotal * (item.percentage / 100);
          } else {
            totalPrice += sign * (item.priceTotal?.netTotal ?? 0);
          }
        }
      });

      if (totalPrice <= 0) {
        warn = '- Der Gesamtpreis muss größer als 0 sein<br><br>';
      }
    }

    if (error) {
      this._dialog.open(KfmInfoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: 'Fehler beim Buchen der Rechnung',
          text: error,
        },
      });
      return { isError: true };
    } else return { isError: false, warnMsg: warn };
  }

  /** Finish task clicked */
  public onFinishTaskClicked(salesItem: Task): {
    isError: boolean;
    warnMsg?: string;
  } {
    let error = '';
    let warn: string | undefined;
    const report = salesItem.report;
    const hasReportText = !!report?.longDesc?.trim();

    if (report && !report.signDate) {
      report.signDate = new Date().toISOString().slice(0, 10);
    }

    if (!salesItem.customerSignature || !salesItem.customerSignature.content) {
      error += '- Es wurde keine Kundenunterschrift erfasst<br>';
    }

    if (!report || !hasReportText) {
      error += '- Der Arbeitsbericht wurde nicht vollständig ausgefüllt<br>';
    }

    if (!salesItem.customer) {
      warn = '- Es wurde kein Kunde ausgewählt<br>';
    }

    if (error) {
      this._dialog.open(KfmInfoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: 'Fehler beim Abschließen des Auftrags',
          text: error,
        },
      });

      return { isError: true };
    } else {
      return { isError: false, warnMsg: warn };
    }
  }

  /** Mechanic signature changed */
  public async onMechanicSignatureChanged(
    salesItem: Task,
    dataUrl: string | null,
  ) {
    if (salesItem.mechanicSignature) {
      salesItem.mechanicSignature.content = dataUrl ?? '';

      if (dataUrl) {
        return await this._mediaService.insertOrUpdate(
          salesItem.mechanicSignature,
        );
      } else {
        // Delete signature
        return await this._mediaService.delete(salesItem.mechanicSignature.uId);
      }
    } else {
      salesItem.mechanicSignature = {
        infoDesc: 'Monteurunterschrift',
        longDesc: 'Auftrag wurde vom Monteur unterschrieben',
        mimeType: 'image/png',
        type: KfmImageType.Monteurunterschrift,
        content: dataUrl ?? '',
        ownerKey: salesItem.uId,
        uId: this._guidGenerator.emptyUUID,
        tenantUId: salesItem.tenantUId,
        userUId: salesItem.userUId,
        stampCreate: new Date().toISOString(),
        syncType: 0,
        concurrencyIdentificator: this._guidGenerator.emptyUUID,
        concurrencyStamp: new Date().toISOString(),
      };

      return await this._mediaService.insertOrUpdate(
        salesItem.mechanicSignature,
      );
    }
  }

  public async onCustomerSignatureChanged(
    salesItem: Task,
    dataUrl: string | null,
  ) {
    if (salesItem.customerSignature) {
      salesItem.customerSignature.content = dataUrl ?? '';

      if (dataUrl) {
        return await this._mediaService.insertOrUpdate(
          salesItem.customerSignature,
        );
      } else {
        // Delete signature
        return await this._mediaService.delete(salesItem.customerSignature.uId);
      }
    } else {
      salesItem.customerSignature = {
        infoDesc: 'Kundenunterschrift',
        longDesc: 'Auftrag wurde vom Kunden unterschrieben',
        mimeType: 'image/png',
        type: KfmImageType.Kundenunterschrift,
        content: dataUrl ?? '',
        ownerKey: salesItem.uId,
        uId: this._guidGenerator.emptyUUID,
        tenantUId: salesItem.tenantUId,
        userUId: salesItem.userUId,
        stampCreate: new Date().toISOString(),
        syncType: 0,
        concurrencyIdentificator: this._guidGenerator.emptyUUID,
        concurrencyStamp: new Date().toISOString(),
      };

      return await this._mediaService.insertOrUpdate(
        salesItem.customerSignature,
      );
    }
  }
}
